import React, { useState } from 'react'
import { 
  IonContent, 
  IonPage, 
  IonButton, 
  IonIcon,
  IonCard,
  IonCardContent,
  IonGrid,
  IonRow,
  IonCol,
  IonPopover,
  IonList,
  IonItem,
  IonLabel
} from '@ionic/react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  peopleOutline,
  trophyOutline,
  chatbubbleOutline,
  statsChartOutline,
  journalOutline,
  calendarOutline,
  shieldCheckmarkOutline,
  phonePortraitOutline,
  checkmarkCircleOutline,
  colorPaletteOutline,
  menuOutline,
  personOutline,
  personAddOutline
} from 'ionicons/icons'
import LanguageSwitcher from '../components/LanguageSwitcher'
import ThemeSwitcher from '../components/ThemeSwitcher'

const LandingPage: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const [isThemeSwitcherOpen, setIsThemeSwitcherOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const features = [
    {
      icon: peopleOutline,
      title: t('landing.features.forCoaches.title'),
      description: t('landing.features.forCoaches.description'),
      benefits: t('landing.features.forCoaches.benefits', { returnObjects: true }) as string[]
    },
    {
      icon: journalOutline,
      title: t('landing.features.forCoachees.title'),
      description: t('landing.features.forCoachees.description'),
      benefits: t('landing.features.forCoachees.benefits', { returnObjects: true }) as string[]
    },
    {
      icon: statsChartOutline,
      title: t('landing.features.analytics.title'),
      description: t('landing.features.analytics.description'),
      benefits: t('landing.features.analytics.benefits', { returnObjects: true }) as string[]
    },
    {
      icon: shieldCheckmarkOutline,
      title: t('landing.features.security.title'),
      description: t('landing.features.security.description'),
      benefits: t('landing.features.security.benefits', { returnObjects: true }) as string[]
    }
  ]

  const testimonials = [
    {
      name: t('landing.testimonials.testimonial1.name'),
      role: t('landing.testimonials.testimonial1.role'),
      content: t('landing.testimonials.testimonial1.content'),
      avatar: "👩‍💼"
    },
    {
      name: t('landing.testimonials.testimonial2.name'),
      role: t('landing.testimonials.testimonial2.role'),
      content: t('landing.testimonials.testimonial2.content'),
      avatar: "👨‍💻"
    },
    {
      name: t('landing.testimonials.testimonial3.name'),
      role: t('landing.testimonials.testimonial3.role'),
      content: t('landing.testimonials.testimonial3.content'),
      avatar: "👩‍⚕️"
    }
  ]

  return (
    <IonPage>
      <IonContent>
        {/* Header/Navigation */}
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <div className="text-xl sm:text-2xl font-heading font-bold text-primary-600">
                  InsightFlow
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4">
                <LanguageSwitcher size="small" />
                <IonButton 
                  fill="clear" 
                  size="small"
                  onClick={() => setIsThemeSwitcherOpen(true)}
                  title={t('theme.customize')}
                >
                  <IonIcon icon={colorPaletteOutline} />
                </IonButton>
                
                {/* Desktop Navigation */}
                <div className="hidden sm:flex items-center space-x-4">
                  <IonButton 
                    fill="clear" 
                    color="primary"
                    size="small"
                    onClick={() => history.push('/login')}
                  >
                    {t('landing.header.signIn')}
                  </IonButton>
                  <IonButton 
                    color="primary"
                    size="small"
                    onClick={() => history.push('/register')}
                  >
                    {t('landing.header.getStarted')}
                  </IonButton>
                </div>

                {/* Mobile Hamburger Menu */}
                <div className="sm:hidden">
                  <IonButton 
                    id="mobile-menu-trigger"
                    fill="clear" 
                    size="small"
                    onClick={() => setIsMobileMenuOpen(true)}
                  >
                    <IonIcon icon={menuOutline} />
                  </IonButton>
                  
                  <IonPopover 
                    isOpen={isMobileMenuOpen}
                    onDidDismiss={() => setIsMobileMenuOpen(false)}
                    trigger="mobile-menu-trigger"
                    dismissOnSelect={true}
                    showBackdrop={true}
                  >
                    <IonList>
                      <IonItem button onClick={() => history.push('/login')}>
                        <IonIcon icon={personOutline} slot="start" />
                        <IonLabel>{t('landing.header.signIn')}</IonLabel>
                      </IonItem>
                      <IonItem button onClick={() => history.push('/register')}>
                        <IonIcon icon={personAddOutline} slot="start" />
                        <IonLabel>{t('landing.header.getStarted')}</IonLabel>
                      </IonItem>
                    </IonList>
                  </IonPopover>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                <span className="block sm:inline">{t('landing.hero.title')}</span>
                <span className="text-primary-600 block sm:inline"> {t('landing.hero.titleCoaching')}</span>
                <br className="hidden sm:block" />
                <span className="text-secondary-600 block sm:inline">{t('landing.hero.titleJourney')}</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-2">
                {t('landing.hero.subtitle')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12 px-4">
                <IonButton 
                  size="large" 
                  color="primary"
                  className="w-full sm:w-auto min-w-0 sm:min-w-48"
                  onClick={() => history.push('/register')}
                >
                  <span className="break-words">{t('landing.hero.startFreeTrial')}</span>
                </IonButton>
                <IonButton 
                  size="large" 
                  fill="outline" 
                  color="primary"
                  className="w-full sm:w-auto min-w-0 sm:min-w-48"
                  onClick={() => history.push('/login')}
                >
                  {t('landing.hero.signIn')}
                </IonButton>
              </div>

              {/* Hero Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-2xl mx-auto px-4">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-primary-600">500+</div>
                  <div className="text-sm sm:text-base text-gray-600 break-words">{t('landing.hero.stats.coaches')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-secondary-600">2,000+</div>
                  <div className="text-sm sm:text-base text-gray-600 break-words">{t('landing.hero.stats.coachees')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-success-600">50,000+</div>
                  <div className="text-sm sm:text-base text-gray-600 break-words">{t('landing.hero.stats.sessions')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-gray-900 mb-3 sm:mb-4 leading-tight px-2">
                {t('landing.features.title')}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-2">
                {t('landing.features.subtitle')}
              </p>
            </div>

            <IonGrid>
              <IonRow>
                {features.map((feature, index) => (
                  <IonCol key={index} size="12" sizeMd="6" className="mb-6 sm:mb-8">
                    <IonCard className="h-full border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                      <IonCardContent className="p-4 sm:p-6 lg:p-8">
                        <div className="flex items-start sm:items-center mb-4">
                          <div className="bg-primary-100 p-2 sm:p-3 rounded-lg mr-3 sm:mr-4 flex-shrink-0">
                            <IonIcon 
                              icon={feature.icon} 
                              className="text-xl sm:text-2xl text-primary-600"
                            />
                          </div>
                          <h3 className="text-lg sm:text-xl lg:text-2xl font-heading font-semibold text-gray-900 leading-tight break-words">
                            {feature.title}
                          </h3>
                        </div>
                        <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed break-words">
                          {feature.description}
                        </p>
                        <ul className="space-y-2">
                          {feature.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start text-sm sm:text-base text-gray-700">
                              <IonIcon 
                                icon={checkmarkCircleOutline} 
                                className="text-success-500 mr-2 mt-0.5 flex-shrink-0"
                              />
                              <span className="break-words">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                ))}
              </IonRow>
            </IonGrid>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="py-12 sm:py-16 lg:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-gray-900 mb-3 sm:mb-4 leading-tight px-2">
                {t('landing.howItWorks.title')}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed px-2">
                {t('landing.howItWorks.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              <div className="text-center px-4">
                <div className="bg-primary-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <span className="text-lg sm:text-2xl font-bold text-primary-600">1</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 break-words">{t('landing.howItWorks.step1.title')}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed break-words">
                  {t('landing.howItWorks.step1.description')}
                </p>
              </div>

              <div className="text-center px-4">
                <div className="bg-secondary-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <span className="text-lg sm:text-2xl font-bold text-secondary-600">2</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 break-words">{t('landing.howItWorks.step2.title')}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed break-words">
                  {t('landing.howItWorks.step2.description')}
                </p>
              </div>

              <div className="text-center px-4">
                <div className="bg-success-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <span className="text-lg sm:text-2xl font-bold text-success-600">3</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 break-words">{t('landing.howItWorks.step3.title')}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed break-words">
                  {t('landing.howItWorks.step3.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-gray-900 mb-3 sm:mb-4 leading-tight px-2">
                {t('landing.testimonials.title')}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed px-2">
                {t('landing.testimonials.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {testimonials.map((testimonial, index) => (
                <IonCard key={index} className="border border-gray-100">
                  <IonCardContent className="p-4 sm:p-6">
                    <div className="flex items-center mb-4">
                      <div className="text-2xl sm:text-3xl mr-3">{testimonial.avatar}</div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-900 text-sm sm:text-base break-words">{testimonial.name}</div>
                        <div className="text-xs sm:text-sm text-gray-600 break-words">{testimonial.role}</div>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base text-gray-700 italic leading-relaxed break-words">"{testimonial.content}"</p>
                  </IonCardContent>
                </IonCard>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-white mb-3 sm:mb-4 leading-tight">
              {t('landing.cta.title')}
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-primary-100 mb-6 sm:mb-8 leading-relaxed">
              {t('landing.cta.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <IonButton 
                size="large" 
                color="light"
                className="w-full sm:w-auto min-w-0 sm:min-w-48"
                onClick={() => history.push('/register')}
              >
                <span className="break-words">{t('landing.cta.startFreeTrial')}</span>
              </IonButton>
              <IonButton 
                size="large" 
                fill="outline" 
                color="light"
                className="w-full sm:w-auto min-w-0 sm:min-w-48"
                onClick={() => history.push('/login')}
              >
                <span className="break-words">{t('landing.cta.signInAccount')}</span>
              </IonButton>
            </div>

            <p className="text-primary-100 text-xs sm:text-sm mt-3 sm:mt-4 leading-relaxed break-words">
              {t('landing.cta.freeTrialNote')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-900 py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-heading font-bold text-white mb-3 sm:mb-4">
                InsightFlow
              </div>
              <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6 leading-relaxed break-words px-2">
                {t('landing.footer.tagline')}
              </p>
              <div className="flex justify-center items-center space-x-4 sm:space-x-6 mb-6 sm:mb-8">
                <IonIcon icon={phonePortraitOutline} className="text-gray-400" />
                <span className="text-sm sm:text-base text-gray-400 break-words">{t('landing.footer.availability')}</span>
              </div>
              
              {/* Legal Links */}
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-6 sm:mb-8">
                <IonButton 
                  fill="clear" 
                  size="small" 
                  color="medium"
                  routerLink="/privacy-policy"
                  className="text-xs sm:text-sm"
                >
                  {t('legal.privacyPolicy')}
                </IonButton>
                <IonButton 
                  fill="clear" 
                  size="small" 
                  color="medium"
                  routerLink="/terms-of-service"
                  className="text-xs sm:text-sm"
                >
                  {t('legal.termsOfService')}
                </IonButton>
                <IonButton 
                  fill="clear" 
                  size="small" 
                  color="medium"
                  routerLink="/gdpr-compliance"
                  className="text-xs sm:text-sm"
                >
                  {t('legal.gdprCompliance')}
                </IonButton>
              </div>
              
              <div className="pt-6 sm:pt-8 border-t border-gray-800 text-center text-xs sm:text-sm text-gray-500 leading-relaxed break-words px-2">
                {t('landing.footer.copyright')}
              </div>
            </div>
          </div>
        </div>

        {/* Theme Switcher Modal */}
        <ThemeSwitcher 
          isOpen={isThemeSwitcherOpen} 
          onClose={() => setIsThemeSwitcherOpen(false)} 
        />
      </IonContent>
    </IonPage>
  )
}

export default LandingPage 